import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Trash2, Save, AlertTriangle, Edit2, Sparkles, BookOpen, Move, MoreVertical, Volume2, Loader2, Printer, Zap } from 'lucide-react';
import { apiClient } from '../services/api';
import { Card, CardUpdate, MeaningWithGrammarRole, WordFormsByPartOfSpeech } from '../types/api';
import { DropdownMenu, DropdownMenuItem, createEditAction, createDeleteAction, createPracticeCardAction } from './DropdownMenu';
import { PracticeCard } from './PracticeCard';
import { UpdateCardAI } from './UpdateCardAI';
import { MoveToModal } from './MoveToModal';
import { PracticePhrases } from './PracticePhrases';
import { GeneratePhrasesModal } from './GeneratePhrasesModal';
import { GenerateCollocationsModal } from './GenerateCollocationsModal';
import { GenerateSynonymsModal } from './GenerateSynonymsModal';
import { GenerateAntonymsModal } from './GenerateAntonymsModal';
import { CardPracticePickerModal } from './CardPracticePickerModal';
import { ContextualPracticeModal } from './ContextualPracticeModal';
import { GenerateImageModal } from './GenerateImageModal';
import { Tooltip } from './Tooltip';
import { SHOW_PRACTICE_PHRASES_IN_MENU, SHOW_PRINT_CARD } from '../constants/uiVisibility';
import { handleConditionalDelete } from '../utils/deleteUtils';
import { speak } from '../utils/speechUtils';
import { TreeItem } from '../types';
import { useInstance } from '../hooks/useInstance';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface CardDetailProps {
  cardId: number;
  onClose: () => void;
  onSave: () => void;
  isEditMode?: boolean;
  isInstanceOwner?: boolean;
  onDelete?: (id: number) => void;
  onMove?: (itemId: number, data: { parent_id?: number | null; category_id?: number | null }) => Promise<void>;
  categories?: Array<{ id: number; name: string }>;
  onEdit?: () => void;
  initialCard?: TreeItem;
  initialCardName?: string;
}

export const CardDetail: React.FC<CardDetailProps> = ({
  cardId,
  onClose,
  onSave,
  isEditMode: initialEditMode = false,
  isInstanceOwner = false,
  onDelete,
  onMove,
  categories = [],
  onEdit,
  initialCard,
  initialCardName,
}) => {
  const { instances, selectedInstanceId } = useInstance();

  const [card, setCard] = useState<Card | null>(null);
  const [parentFolder, setParentFolder] = useState<Card | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showSavedState, setShowSavedState] = useState(false);
  const [showPracticeCard, setShowPracticeCard] = useState(false);
  const [showUpdateAI, setShowUpdateAI] = useState(false);
  const [showMoveToModal, setShowMoveToModal] = useState(false);
  const [showPracticePhrases, setShowPracticePhrases] = useState(false);
  const [showGeneratePhrases, setShowGeneratePhrases] = useState(false);
  const [showCollocations, setShowCollocations] = useState(false);
  const [showSynonyms, setShowSynonyms] = useState(false);
  const [showAntonyms, setShowAntonyms] = useState(false);
  const [showCardPracticePicker, setShowCardPracticePicker] = useState(false);
  const [showCardContextualPractice, setShowCardContextualPractice] = useState(false);
  const [showGenerateImage, setShowGenerateImage] = useState(false);
  const [pdfToast, setPdfToast] = useState<string | null>(null);
  const [internalEditMode, setInternalEditMode] = useState(initialEditMode);
  const savedStateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const initialCardUsedRef = useRef(false);

  const speakText = (text: string) => {
    speak(text, { lang: 'en-US' });
  };

  const categoryName = card?.category_id ? categories.find(c => c.id === card.category_id)?.name ?? null : null;
  const collectionName = instances.find(i => i.id === selectedInstanceId)?.name ?? null;

  // Use internal edit mode if set, otherwise use prop
  const isEditMode = internalEditMode || initialEditMode;

  // Reset internal edit mode when cardId changes
  useEffect(() => {
    setInternalEditMode(initialEditMode);
  }, [cardId, initialEditMode]);

  // Lock body scroll while modal is open
  useBodyScrollLock();

  // Form state
  const [name, setName] = useState('');
  const [examplePhrases, setExamplePhrases] = useState<string[]>([]);
  const [newUserPhrase, setNewUserPhrase] = useState('');
  const [isSavingPhrases, setIsSavingPhrases] = useState(false);
  const [meanings, setMeanings] = useState<MeaningWithGrammarRole[]>([]);
  const [grammarRoles, setGrammarRoles] = useState<string[]>([]); // DEPRECATED - kept for backward compatibility
  const [collocations, setCollocations] = useState<string[]>([]);
  const [synonyms, setSynonyms] = useState<string[]>([]);
  const [antonyms, setAntonyms] = useState<string[]>([]);
  const [relatedWords, setRelatedWords] = useState<string[]>([]);
  const [wordForms, setWordForms] = useState<WordFormsByPartOfSpeech | null>(null);
  const [videos, setVideos] = useState<string[]>([]);
  const [useCount, setUseCount] = useState(0);
  const [ipa, setIpa] = useState('');
  const [register, setRegister] = useState('');
  const [difficulty, setDifficulty] = useState<string>('');
  const [etymology, setEtymology] = useState('');
  const [masteryLevel, setMasteryLevel] = useState<string>('');
  const [lastReviewedAt, setLastReviewedAt] = useState<string | null>(null);
  const [notes, setNotes] = useState('');

  // Input states for adding new list items
  const [newExamplePhrase, setNewExamplePhrase] = useState('');
  const [newMeaning, setNewMeaning] = useState('');
  const [newGrammarRole, setNewGrammarRole] = useState('');
  const [newCollocation, setNewCollocation] = useState('');
  const [newSynonym, setNewSynonym] = useState('');
  const [newAntonym, setNewAntonym] = useState('');
  const [newRelatedWord, setNewRelatedWord] = useState('');
  const [newWordForm, setNewWordForm] = useState('');
  const [newVideo, setNewVideo] = useState('');

  useEffect(() => {
    loadCard();
  }, [cardId]);

  // Parse meanings from API response - handles both old and new formats
  const parseMeanings = (cardData: Card): MeaningWithGrammarRole[] => {
    if (!cardData.meanings || cardData.meanings.length === 0) {
      return [];
    }

    return cardData.meanings.map((meaning, index) => {
      // Handle old format (string)
      if (typeof meaning === 'string') {
        // Try to get grammar role from grammar_roles array if available
        const grammarRole = cardData.grammar_roles && cardData.grammar_roles[index] 
          ? cardData.grammar_roles[index] 
          : 'noun'; // Default fallback
        
        return {
          grammar_role: grammarRole,
          meaning: meaning,
          example_phrases: [] // Old format didn't have per-meaning examples
        };
      }
      
      // Handle new format (object)
      return {
        grammar_role: meaning.grammar_role || 'noun',
        meaning: meaning.meaning || '',
        example_phrases: meaning.example_phrases || []
      };
    });
  };

  const wordFormsEqual = (
    a: WordFormsByPartOfSpeech | null,
    b: WordFormsByPartOfSpeech | null
  ): boolean => {
    const keys: (keyof WordFormsByPartOfSpeech)[] = ['noun', 'verb', 'adjective', 'adverb'];
    const getArray = (obj: WordFormsByPartOfSpeech | null | undefined, key: keyof WordFormsByPartOfSpeech) =>
      (obj && Array.isArray(obj[key])) ? (obj[key] as string[]) : [];

    return keys.every((key) => {
      const arrA = getArray(a, key);
      const arrB = getArray(b, key);
      if (arrA.length !== arrB.length) return false;
      return arrA.every((val, idx) => val === arrB[idx]);
    });
  };

  const normalizeWordFormsForUpdate = (
    wordForms: WordFormsByPartOfSpeech | null
  ): WordFormsByPartOfSpeech | null => {
    if (!wordForms) return null;
    const keys: (keyof WordFormsByPartOfSpeech)[] = ['noun', 'verb', 'adjective', 'adverb'];
    const normalized: WordFormsByPartOfSpeech = {};
    let hasAny = false;

    keys.forEach((key) => {
      const value = wordForms[key];
      if (Array.isArray(value) && value.length > 0) {
        normalized[key] = value;
        hasAny = true;
      }
    });

    return hasAny ? normalized : null;
  };

  const populateFormState = (cardData: Card) => {
    setCard(cardData);
    setName(cardData.name);
    setExamplePhrases(Array.isArray(cardData.example_phrases) ? cardData.example_phrases : []);
    setGrammarRoles(Array.isArray(cardData.grammar_roles) ? cardData.grammar_roles : []);
    const parsedMeanings = parseMeanings(cardData);
    setMeanings(parsedMeanings);
    setCollocations(Array.isArray(cardData.collocations) ? cardData.collocations : []);
    setSynonyms(Array.isArray(cardData.synonyms) ? cardData.synonyms : []);
    setAntonyms(Array.isArray(cardData.antonyms) ? cardData.antonyms : []);
    setRelatedWords(Array.isArray(cardData.related_words) ? cardData.related_words : []);
    setWordForms(cardData.word_forms || null);
    setVideos(Array.isArray(cardData.videos) ? cardData.videos : []);
    setUseCount(cardData.use_count || 0);
    setIpa(cardData.ipa || '');
    setRegister(cardData.register || '');
    setDifficulty(cardData.difficulty || '');
    setEtymology(cardData.etymology || '');
    setMasteryLevel(cardData.mastery_level || '');
    setLastReviewedAt(cardData.last_reviewed_at || null);
    setNotes(cardData.notes || '');
  };

  const loadCard = async () => {
    if (initialCard && !initialCardUsedRef.current) {
      initialCardUsedRef.current = true;
      populateFormState(initialCard as unknown as Card);
      setIsLoading(false);
      setIsLoadingContent(true);
      // Background fetch to hydrate full card data (tree data may not include all fields)
      apiClient.getCard(cardId).then((fullCard) => {
        populateFormState(fullCard);
        if (fullCard.parent_id) {
          apiClient.getCard(fullCard.parent_id).then(setParentFolder).catch(() => {});
        }
      }).catch(() => {}).finally(() => setIsLoadingContent(false));
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const cardData = await apiClient.getCard(cardId);
      populateFormState(cardData);
      if (cardData.parent_id) {
        const parentData = await apiClient.getCard(cardData.parent_id);
        setParentFolder(parentData);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load card');
      console.error('Error loading card:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if there are unsaved changes
  const hasUnsavedChanges = (): boolean => {
    if (!card) return false;

    // Compare arrays
    const arraysEqual = (a: string[] | null, b: string[] | null): boolean => {
      const arrA = a || [];
      const arrB = b || [];
      if (arrA.length !== arrB.length) return false;
      return arrA.every((val, idx) => val === arrB[idx]);
    };

    // Compare meanings arrays (handle both old and new formats)
    const meaningsEqual = (a: MeaningWithGrammarRole[], b: (string | MeaningWithGrammarRole)[] | null): boolean => {
      const arrB = b || [];
      if (a.length !== arrB.length) return false;
      
      return a.every((meaningA, idx) => {
        const meaningB = arrB[idx];
        if (typeof meaningB === 'string') {
          // Old format - compare meaning text only
          return meaningA.meaning === meaningB;
        }
        // New format - compare all fields
        return meaningA.grammar_role === meaningB.grammar_role &&
               meaningA.meaning === meaningB.meaning &&
               arraysEqual(meaningA.example_phrases, meaningB.example_phrases);
      });
    };

    return (
      name !== card.name ||
      !meaningsEqual(meanings, card.meanings) ||
      !arraysEqual(collocations, card.collocations) ||
      !arraysEqual(synonyms, card.synonyms) ||
      !arraysEqual(antonyms, card.antonyms) ||
      !arraysEqual(relatedWords, card.related_words) ||
      !wordFormsEqual(wordForms, card.word_forms) ||
      !arraysEqual(videos, card.videos) ||
      (ipa || '') !== (card.ipa || '') ||
      (register || '') !== (card.register || '') ||
      (difficulty || '') !== (card.difficulty || '') ||
      (etymology || '') !== (card.etymology || '') ||
      (masteryLevel || '') !== (card.mastery_level || '') ||
      (notes || '') !== (card.notes || '') ||
      useCount !== card.use_count
    );
  };

  const handleClose = () => {
    if (isEditMode && hasUnsavedChanges()) {
      setShowCloseConfirm(true);
    } else {
      setInternalEditMode(false);
      onClose();
    }
  };

  const handleSave = async () => {
    if (!card) return;

    try {
      setIsSaving(true);
      setError(null);

      const updateData: CardUpdate = {
        name: name !== card.name ? name : undefined,
        // Convert meanings to new format for API
        meanings: meanings.length > 0 ? meanings.map(m => ({
          grammar_role: m.grammar_role,
          meaning: m.meaning,
          example_phrases: m.example_phrases
        })) : null,
        example_phrases: examplePhrases,
        collocations: collocations.length > 0 ? collocations : null,
        synonyms: synonyms.length > 0 ? synonyms : null,
        antonyms: antonyms.length > 0 ? antonyms : null,
        related_words: relatedWords.length > 0 ? relatedWords : null,
        word_forms: normalizeWordFormsForUpdate(wordForms),
        videos: videos.length > 0 ? videos : null,
        ipa: ipa || null,
        register: register || null,
        difficulty: difficulty || null,
        etymology: etymology || null,
        mastery_level: masteryLevel || null,
        notes: notes || null,
        use_count: useCount !== card.use_count ? useCount : undefined,
      };

      // Remove undefined fields
      Object.keys(updateData).forEach(key => {
        if (updateData[key as keyof CardUpdate] === undefined) {
          delete updateData[key as keyof CardUpdate];
        }
      });

      if (Object.keys(updateData).length > 0) {
        await apiClient.updateCard(cardId, updateData);
        // Reload card to get updated data
        await loadCard();
      }

      // Show saved state
      setShowSavedState(true);
      if (savedStateTimeoutRef.current) {
        clearTimeout(savedStateTimeoutRef.current);
      }
      savedStateTimeoutRef.current = setTimeout(() => {
        setShowSavedState(false);
      }, 3000);

      // Switch back to read-only mode after saving
      setInternalEditMode(false);
      onSave();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save card');
      console.error('Error saving card:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndClose = async () => {
    await handleSave();
    // Small delay to show saved state before closing
    setTimeout(() => {
      setInternalEditMode(false);
      onClose();
    }, 500);
  };

  const handleDiscard = () => {
    setShowCloseConfirm(false);
    onClose();
  };

  const handleEdit = () => {
    // Switch to edit mode
    setInternalEditMode(true);
    if (onEdit) {
      onEdit();
    }
  };

  const handlePrint = () => {
    if (!card) return;
    const newTab = window.open(apiClient.getCardPdfUrl(card.id), '_blank');
    if (!newTab) {
      setPdfToast('Could not generate PDF. Please try again.');
      setTimeout(() => setPdfToast(null), 4000);
    }
  };

  const handleAddPhraseFromGenerated = async (phrase: string) => {
    if (!card) return;
    const updated = [...examplePhrases, phrase];
    await apiClient.updateCard(cardId, { example_phrases: updated });
    setExamplePhrases(updated);
  };

  const handleAddSynonymFromGenerated = async (synonym: string) => {
    if (!card) return;
    const data = await apiClient.addSynonym(card.id, synonym);
    setSynonyms(data.synonyms);
  };

  const handleAddAntonymFromGenerated = async (antonym: string) => {
    if (!card) return;
    const data = await apiClient.addAntonym(card.id, antonym);
    setAntonyms(data.antonyms);
  };

  const handleAddCollocationFromGenerated = async (collocation: string) => {
    if (!card) return;
    const data = await apiClient.addCollocation(card.id, collocation);
    setCollocations(data.collocations);
  };

  const handleAddPhrase = async () => {
    if (!newUserPhrase.trim() || !card) return;
    const updated = [...examplePhrases, newUserPhrase.trim()];
    setIsSavingPhrases(true);
    try {
      await apiClient.updateCard(cardId, { example_phrases: updated });
      setExamplePhrases(updated);
      setNewUserPhrase('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save phrase');
    } finally {
      setIsSavingPhrases(false);
    }
  };

  const handleRemovePhrase = async (index: number) => {
    if (!card) return;
    const updated = examplePhrases.filter((_, i) => i !== index);
    setIsSavingPhrases(true);
    try {
      await apiClient.updateCard(cardId, { example_phrases: updated });
      setExamplePhrases(updated);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to remove phrase');
    } finally {
      setIsSavingPhrases(false);
    }
  };

  const handleDelete = async () => {
    if (!card || !onDelete) return;
    
    const cardAsTreeItem: TreeItem = {
      id: card.id,
      name: card.name,
      type: 'card',
      parent_id: card.parent_id,
      is_folder: false,
      children: [],
      category_id: card.category_id ?? undefined,
    };

    await handleConditionalDelete(
      cardAsTreeItem,
      () => {
        onDelete(card.id);
        onClose();
      },
      (error) => {
        console.error('Error deleting card:', error);
        setError('Failed to delete card');
      }
    );
  };

  const handleMove = async (itemId: number, data: { parent_id?: number | null; category_id?: number | null }) => {
    if (!onMove) return;
    try {
      await onMove(itemId, data);
      setShowMoveToModal(false);
      onClose();
      onSave(); // Refresh the view
    } catch (error) {
      console.error('Error moving card:', error);
      setError('Failed to move card');
    }
  };

  // AI actions — same for owners and viewers
  const getAIMenuItems = (): DropdownMenuItem[] => {
    const items: DropdownMenuItem[] = [];
    if (isInstanceOwner) {
      items.push({
        id: 'update-ai',
        label: 'Update card using AI',
        icon: <Sparkles className="h-4 w-4" />,
        onClick: () => setShowUpdateAI(true),
      });
    }
    items.push(
      {
        id: 'generate-phrases',
        label: 'Generate phrases',
        icon: <Sparkles className="h-4 w-4" />,
        onClick: () => setShowGeneratePhrases(true),
      },
      {
        id: 'collocations',
        label: 'Generate collocations',
        icon: <BookOpen className="h-4 w-4" />,
        onClick: () => setShowCollocations(true),
      },
      {
        id: 'synonyms',
        label: 'Generate synonyms',
        icon: <BookOpen className="h-4 w-4" />,
        onClick: () => setShowSynonyms(true),
      },
      {
        id: 'antonyms',
        label: 'Generate antonyms',
        icon: <BookOpen className="h-4 w-4" />,
        onClick: () => setShowAntonyms(true),
      },
    );
    if (SHOW_PRACTICE_PHRASES_IN_MENU) {
      items.push({
        id: 'practice-phrases',
        label: 'Practice Phrases',
        icon: <Sparkles className="h-4 w-4" />,
        onClick: () => setShowPracticePhrases(true),
      });
    }
    return items;
  };

  // Structural actions — owners only
  const getStructuralMenuItems = (): DropdownMenuItem[] => {
    const items: DropdownMenuItem[] = [
      createEditAction(handleEdit),
      {
        id: 'move',
        label: 'Move To...',
        icon: <Move className="h-4 w-4" />,
        onClick: () => setShowMoveToModal(true),
      },
    ];
    if (SHOW_PRINT_CARD) {
      items.push({
        id: 'print',
        label: 'Print / Download PDF',
        icon: <Printer className="h-4 w-4" />,
        onClick: handlePrint,
      });
    }
    items.push(createDeleteAction(handleDelete));
    return items;
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (savedStateTimeoutRef.current) {
        clearTimeout(savedStateTimeoutRef.current);
      }
    };
  }, []);

  // Helper functions to add/remove items from lists
  const addToList = (list: string[], setList: (items: string[]) => void, value: string, setValue: (val: string) => void) => {
    if (value.trim()) {
      setList([...list, value.trim()]);
      setValue('');
    }
  };

  const removeFromList = (list: string[], setList: (items: string[]) => void, index: number) => {
    setList(list.filter((_, i) => i !== index));
  };

  // State for adding example phrases to meanings (index -> input value)
  const [meaningExampleInputs, setMeaningExampleInputs] = useState<Record<number, string>>({});

  // Render editable meaning with grammar role and example phrases
  const renderEditableMeaning = (meaning: MeaningWithGrammarRole, index: number) => {
    const exampleInputValue = meaningExampleInputs[index] || '';
    
    return (
      <div key={index} className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-md mb-3">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            {/* Grammar Role Badge */}
            <div className="mb-2">
              <input
                type="text"
                value={meaning.grammar_role}
                onChange={(e) => {
                  const updated = [...meanings];
                  updated[index] = { ...meaning, grammar_role: e.target.value };
                  setMeanings(updated);
                }}
                placeholder="Grammar role (e.g., noun, verb)"
                className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-md border-0 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            
            {/* Meaning Text */}
            <textarea
              value={meaning.meaning}
              onChange={(e) => {
                const updated = [...meanings];
                updated[index] = { ...meaning, meaning: e.target.value };
                setMeanings(updated);
              }}
              placeholder="Meaning/definition"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none mb-2"
              rows={2}
            />
            
            {/* Example Phrases */}
            <div className="mt-2">
              <label className="text-xs font-medium text-gray-600 mb-1 block">Example Phrases:</label>
              <div className="space-y-1">
                {meaning.example_phrases.map((phrase, phraseIndex) => (
                  <div key={phraseIndex} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={phrase}
                      onChange={(e) => {
                        const updated = [...meanings];
                        const updatedPhrases = [...meaning.example_phrases];
                        updatedPhrases[phraseIndex] = e.target.value;
                        updated[index] = { ...meaning, example_phrases: updatedPhrases };
                        setMeanings(updated);
                      }}
                      className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 italic"
                      placeholder="Example phrase"
                    />
                    <button
                      onClick={() => {
                        const updated = [...meanings];
                        updated[index] = {
                          ...meaning,
                          example_phrases: meaning.example_phrases.filter((_, i) => i !== phraseIndex)
                        };
                        setMeanings(updated);
                      }}
                      className="p-1 hover:bg-red-200 rounded transition-colors"
                      title="Remove phrase"
                    >
                      <Trash2 className="h-3 w-3 text-red-600" />
                    </button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={exampleInputValue}
                    onChange={(e) => {
                      setMeaningExampleInputs({ ...meaningExampleInputs, [index]: e.target.value });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (exampleInputValue.trim()) {
                          const updated = [...meanings];
                          updated[index] = {
                            ...meaning,
                            example_phrases: [...meaning.example_phrases, exampleInputValue.trim()]
                          };
                          setMeanings(updated);
                          setMeaningExampleInputs({ ...meaningExampleInputs, [index]: '' });
                        }
                      }
                    }}
                    placeholder="Add example phrase..."
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <button
                    onClick={() => {
                      if (exampleInputValue.trim()) {
                        const updated = [...meanings];
                        updated[index] = {
                          ...meaning,
                          example_phrases: [...meaning.example_phrases, exampleInputValue.trim()]
                        };
                        setMeanings(updated);
                        setMeaningExampleInputs({ ...meaningExampleInputs, [index]: '' });
                      }
                    }}
                    className="px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={() => {
              setMeanings(meanings.filter((_, i) => i !== index));
              // Clean up input state
              const newInputs = { ...meaningExampleInputs };
              delete newInputs[index];
              setMeaningExampleInputs(newInputs);
            }}
            className="ml-2 p-1 hover:bg-red-200 rounded transition-colors flex-shrink-0"
            title="Remove meaning"
          >
            <Trash2 className="h-4 w-4 text-red-600" />
          </button>
        </div>
      </div>
    );
  };

  // Render view-only meaning with grammar role and example phrases
  const renderViewMeaning = (meaning: MeaningWithGrammarRole, index: number) => (
    <div key={index} className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-md mb-3">
      {/* Grammar Role Badge */}
      <div className="mb-2">
        <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-md">
          {meaning.grammar_role}
        </span>
      </div>
      
      {/* Meaning Text */}
      <p className="text-gray-900 leading-relaxed mb-2">{meaning.meaning}</p>
      
      {/* Example Phrases */}
      {meaning.example_phrases && meaning.example_phrases.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-medium text-gray-600 mb-1">Examples:</p>
          <ul className="list-disc list-inside space-y-1">
            {meaning.example_phrases.map((phrase, phraseIndex) => (
              <li key={phraseIndex} className="text-sm text-gray-700 italic">
                "{phrase}"
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  // Ensure we always have an array (API may return object or other non-array)
  const ensureStringArray = (value: unknown): string[] =>
    Array.isArray(value) ? value : [];

  const renderWordFormsSection = () => {
    const entries: {
      label: string;
      key: keyof WordFormsByPartOfSpeech;
      badgeClass: string;
    }[] = [
      { label: 'Noun', key: 'noun', badgeClass: 'bg-blue-100 text-blue-800' },
      { label: 'Verb', key: 'verb', badgeClass: 'bg-purple-100 text-purple-800' },
      { label: 'Adjective', key: 'adjective', badgeClass: 'bg-green-100 text-green-800' },
      { label: 'Adverb', key: 'adverb', badgeClass: 'bg-yellow-100 text-yellow-800' },
    ];

    const hasAny =
      wordForms &&
      entries.some(
        ({ key }) => Array.isArray(wordForms?.[key]) && (wordForms?.[key] as string[]).length > 0
      );

    if (!hasAny) {
      return <p className="text-xs text-gray-400 italic">None</p>;
    }

    return (
      <div className="space-y-1">
        {entries.map(({ label, key, badgeClass }) => {
          const forms =
            wordForms && Array.isArray(wordForms[key]) ? (wordForms[key] as string[]) : [];
          if (forms.length === 0) return null;

          return (
            <div key={key} className="flex items-baseline gap-2 text-sm text-gray-700">
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${badgeClass}`}
              >
                {label.toLowerCase()}
              </span>
              <span className="text-gray-800">{forms.join(', ')}</span>
            </div>
          );
        })}
      </div>
    );
  };

  // Render editable list field (for other fields like collocations, synonyms, etc.)
  const renderEditableList = (
    label: string,
    items: string[],
    setItems: (items: string[]) => void,
    newValue: string,
    setNewValue: (val: string) => void,
    bgColor: string = 'bg-blue-50',
    borderColor: string = 'border-blue-500'
  ) => {
    const safeItems = ensureStringArray(items);
    return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-3">{label}</h2>
      <div className="space-y-2">
        {safeItems.map((item, index) => (
          <div key={index} className={`${bgColor} border-l-4 ${borderColor} p-3 rounded-r-md flex items-center justify-between`}>
            <p className="text-gray-900 leading-relaxed flex-1">{item}</p>
            <button
              onClick={() => removeFromList(safeItems, setItems, index)}
              className="ml-2 p-1 hover:bg-red-200 rounded transition-colors"
              title="Remove"
            >
              <Trash2 className="h-4 w-4 text-red-600" />
            </button>
          </div>
        ))}
        <div className="flex gap-2">
          <input
            type="text"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addToList(safeItems, setItems, newValue, setNewValue);
              }
            }}
            placeholder={`Add new ${label.toLowerCase()}...`}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={() => addToList(safeItems, setItems, newValue, setNewValue)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
        {safeItems.length === 0 && newValue === '' && (
          <p className="text-sm text-gray-400 italic">No {label.toLowerCase()} added yet</p>
        )}
      </div>
    </div>
  );
  };

  // Render view-only list field
  const renderViewList = (
    label: string,
    items: string[],
    bgColor: string = 'bg-blue-50',
    borderColor: string = 'border-blue-500'
  ) => {
    const safeItems = ensureStringArray(items);
    return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-3">{label}</h2>
      {safeItems.length > 0 ? (
        <div className="space-y-3">
          {safeItems.map((item, index) => (
            <div key={index} className={`${bgColor} border-l-4 ${borderColor} p-4 rounded-r-md`}>
              <p className="text-gray-900 leading-relaxed">{item}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-400 italic">No {label.toLowerCase()} added yet</p>
      )}
    </div>
  );
  };

  // Render editable chip list
  const renderEditableChipList = (
    label: string,
    items: string[],
    setItems: (items: string[]) => void,
    newValue: string,
    setNewValue: (val: string) => void,
    chipColor: string = 'bg-green-100 text-green-800'
  ) => {
    const safeItems = ensureStringArray(items);
    return (
    <div>
      <h3 className="text-sm font-medium text-gray-500 mb-2">{label}</h3>
      <div className="flex flex-wrap gap-2 mb-2">
        {safeItems.map((item, index) => (
            <span key={index} className={`px-3 py-1 ${chipColor} rounded-full text-sm flex items-center gap-1`}>
            {item}
            {isEditMode && (
              <button
                onClick={() => removeFromList(safeItems, setItems, index)}
                className="ml-1 hover:text-red-600"
                title="Remove"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
      </div>
      {isEditMode && (
        <div className="flex gap-2">
          <input
            type="text"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addToList(safeItems, setItems, newValue, setNewValue);
              }
            }}
            placeholder={`Add ${label.toLowerCase()}...`}
            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <button
            onClick={() => addToList(safeItems, setItems, newValue, setNewValue)}
            className="px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-3 w-3" />
          </button>
        </div>
      )}
      {safeItems.length === 0 && !isEditMode && (
        <p className="text-xs text-gray-400 italic">None</p>
      )}
    </div>
  );
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
          {initialCardName && (
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 truncate">{initialCardName}</h2>
            </div>
          )}
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            <div className="text-gray-600">Loading card details...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !card) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
          <div className="text-red-600 mb-4">{error}</div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-hidden">
        <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 my-8 flex flex-col max-h-[90vh]">
          {/* Sticky Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 z-10 px-6 pt-3 pb-4">
            {/* Action bar row */}
            <div className="flex items-center justify-between mb-2">
              <div>
                {card && !card.is_folder && !isEditMode && (
                  <button
                    onClick={() => speakText(name)}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                    title="Hear pronunciation"
                  >
                    <Volume2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                {isEditMode && (
                  <>
                    <button
                      onClick={handleClose}
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      Close
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={isSaving || !hasUnsavedChanges()}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
                    >
                      {isSaving ? (
                        <>
                          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-3.5 w-3.5" />
                          Save
                        </>
                      )}
                    </button>
                  </>
                )}
                {!isEditMode && card && !card.is_folder && (
                  <>
                    {/* Zap — unified practice + AI modal */}
                    <button
                      onClick={() => setShowCardPracticePicker(true)}
                      className="p-2 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors"
                      title="Practice & Actions"
                    >
                      <Zap className="h-5 w-5 text-amber-600" />
                    </button>

                    {/* Structural actions (owners only) */}
                    {isInstanceOwner && (
                      <DropdownMenu
                        items={getStructuralMenuItems()}
                        className="opacity-100"
                      />
                    )}

                    {/* Close */}
                    <Tooltip text="Close">
                      <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors ml-1"
                      >
                        <X className="h-5 w-5 text-gray-600" />
                      </button>
                    </Tooltip>
                  </>
                )}
                {!isEditMode && (!card || card.is_folder) && (
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    title="Close"
                  >
                    <X className="h-5 w-5 text-gray-600" />
                  </button>
                )}
              </div>
            </div>

            {/* Title + breadcrumb */}
            {isEditMode ? (
              <div>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="text-xl font-bold text-gray-900 w-full border-0 border-b-2 border-transparent focus:border-blue-500 focus:outline-none pb-1 transition-colors"
                  placeholder="Card name"
                />
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-sm text-gray-400 truncate">
                    {[parentFolder?.name, categoryName, collectionName].filter(Boolean).join(' · ')}
                  </p>
                  {showSavedState && (
                    <span className="text-xs text-green-600 font-medium flex-shrink-0">All changes saved</span>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <h1 className="text-xl font-bold text-gray-900 leading-snug">{name}</h1>
                {(parentFolder || categoryName || collectionName) && (
                  <p className="text-sm text-gray-400 mt-0.5 truncate">
                    {[parentFolder?.name, categoryName, collectionName].filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
          {isLoadingContent ? (
            <div className="flex justify-center pt-8">
              <Loader2 className="h-5 w-5 animate-spin text-gray-300" />
            </div>
          ) : (
          <div className="space-y-8">
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm">
              {error}
            </div>
          )}

          {/* AI enrichment prompt for empty cards */}
          {!isEditMode && isInstanceOwner && meanings.length === 0 && !showUpdateAI && (
            <div className="flex items-center justify-between bg-purple-50 border border-purple-200 rounded-lg px-4 py-3">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-purple-500 flex-shrink-0" />
                <p className="text-sm text-purple-700">This card has no content yet.</p>
              </div>
              <button
                onClick={() => setShowUpdateAI(true)}
                className="ml-4 flex-shrink-0 text-sm font-medium text-purple-700 hover:text-purple-900 underline"
              >
                Enrich with AI
              </button>
            </div>
          )}

          {/* Card Image — read mode */}
          {!isEditMode && card?.images && card.images.length > 0 && (
            <div className="-mx-6 -mt-2 mb-2">
              <img
                src={card.images[0]}
                alt={`Illustration for "${name}"`}
                className="w-full max-h-64 object-contain bg-gray-50"
                style={{ borderRadius: '0 0 12px 12px' }}
              />
            </div>
          )}

          {/* Card Image — edit mode */}
          {isEditMode && card?.images && card.images.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Image</h2>
              <div className="flex items-start gap-3">
                <img
                  src={card.images[0]}
                  alt={`Illustration for "${name}"`}
                  className="w-32 h-32 object-contain bg-gray-50 rounded-lg border border-gray-200 flex-shrink-0"
                />
                <div className="flex flex-col justify-between h-32">
                  <p className="text-xs text-gray-400 leading-snug">AI-generated illustration</p>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!card.images?.[0]) return;
                      try {
                        const result = await apiClient.deleteImage(card.id, card.images[0]);
                        setCard(prev => prev ? { ...prev, images: result.images } : prev);
                      } catch {
                        setError('Failed to delete image. Please try again.');
                      }
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors self-start"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete image
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Definitions & Examples */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Definitions & Examples</h2>
            {meanings.length > 0 ? (
              <div className="space-y-3">
                {meanings.map((meaning, index) =>
                  isEditMode
                    ? renderEditableMeaning(meaning, index)
                    : renderViewMeaning(meaning, index)
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">No definitions added yet</p>
            )}
            
            {/* Add new meaning button (edit mode only) */}
            {isEditMode && (
              <div className="mt-4 p-4 border-2 border-dashed border-gray-300 rounded-md">
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newGrammarRole}
                      onChange={(e) => setNewGrammarRole(e.target.value)}
                      placeholder="Grammar role (e.g., noun, verb)"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <textarea
                    value={newMeaning}
                    onChange={(e) => setNewMeaning(e.target.value)}
                    placeholder="Meaning/definition"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    rows={2}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        if (newMeaning.trim()) {
                          setMeanings([...meanings, {
                            grammar_role: newGrammarRole.trim() || 'noun',
                            meaning: newMeaning.trim(),
                            example_phrases: []
                          }]);
                          setNewMeaning('');
                          setNewGrammarRole('');
                        }
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Add Meaning
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Secondary Reference Data */}
          <div className="space-y-6 pt-4 border-t border-gray-100">
          {/* Synonyms, Antonyms, Collocations, Related Expressions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderEditableChipList('Synonyms', synonyms, setSynonyms, newSynonym, setNewSynonym, 'bg-green-100 text-green-800')}
              {renderEditableChipList('Antonyms', antonyms, setAntonyms, newAntonym, setNewAntonym, 'bg-red-100 text-red-800')}
              {renderEditableChipList('Collocations', collocations, setCollocations, newCollocation, setNewCollocation, 'bg-indigo-100 text-indigo-800')}
              {renderEditableChipList('Related Expressions', relatedWords, setRelatedWords, newRelatedWord, setNewRelatedWord, 'bg-yellow-100 text-yellow-800')}
            </div>

            {/* Grammar Roles */}
            {renderEditableChipList('Grammar Roles', grammarRoles, setGrammarRoles, newGrammarRole, setNewGrammarRole, 'bg-gray-100 text-gray-700')}

            {/* Word Forms (grouped by part of speech) */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Word Forms</h3>
              {renderWordFormsSection()}
            </div>

            {/* Videos */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Videos</h3>
              <div className="space-y-2 mb-2">
                {videos.map((video, index) => (
                  <div key={index} className="flex items-center justify-between">
                    {isEditMode ? (
                      <>
                        <input
                          type="text"
                          value={video}
                          onChange={(e) => {
                            const updated = [...videos];
                            updated[index] = e.target.value;
                            setVideos(updated);
                          }}
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                        <button
                          onClick={() => removeFromList(videos, setVideos, index)}
                          className="ml-2 p-1 hover:bg-red-200 rounded transition-colors"
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </button>
                      </>
                    ) : (
                      <a
                        href={video}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-sm text-blue-600 hover:text-blue-800 hover:underline break-all"
                      >
                        {video}
                      </a>
                    )}
                  </div>
                ))}
              </div>
              {isEditMode && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newVideo}
                    onChange={(e) => setNewVideo(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addToList(videos, setVideos, newVideo, setNewVideo);
                      }
                    }}
                    placeholder="Add video URL..."
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <button
                    onClick={() => addToList(videos, setVideos, newVideo, setNewVideo)}
                    className="px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                </div>
              )}
              {videos.length === 0 && !isEditMode && (
                <p className="text-xs text-gray-400 italic">None</p>
              )}
            </div>

            {/* Metadata */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-100">
              <div>
                <p className="text-xs text-gray-500 mb-1">IPA</p>
                {isEditMode ? (
                  <input
                    type="text"
                    value={ipa}
                    onChange={(e) => setIpa(e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
                    placeholder="IPA"
                  />
                ) : (
                  ipa && <p className="text-sm text-gray-700 font-mono">{ipa}</p>
                )}
              </div>
              {useCount > 0 && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Times Used</p>
                  <p className="text-sm text-gray-700">{useCount}</p>
                </div>
              )}
              {lastReviewedAt && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Last Reviewed</p>
                  <p className="text-sm text-gray-700">
                    {new Date(lastReviewedAt).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>

            {/* Etymology */}
            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Etymology</h3>
              {isEditMode ? (
                <textarea
                  value={etymology}
                  onChange={(e) => setEtymology(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  rows={3}
                  placeholder="Etymology"
                />
              ) : (
                etymology ? (
                  <p className="text-sm text-gray-700 leading-relaxed">{etymology}</p>
                ) : (
                  <p className="text-xs text-gray-400 italic">None</p>
                )
              )}
            </div>

            {/* Notes */}
            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Notes</h3>
              {isEditMode ? (
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  rows={4}
                  placeholder="Notes"
                />
              ) : (
                notes ? (
                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{notes}</p>
                ) : (
                  <p className="text-xs text-gray-400 italic">None</p>
                )
              )}
            </div>

            {/* My Phrases */}
            {card && !card.is_folder && (
              <div className="pt-4 border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-500 mb-3">My Phrases</h3>
                <div className="space-y-2 mb-3">
                  {examplePhrases.map((phrase, index) => (
                    <div key={index} className="flex items-start gap-2 bg-amber-50 border-l-4 border-amber-400 p-3 rounded-r-md">
                      <p className="text-sm text-gray-800 italic flex-1">"{phrase}"</p>
                      <button
                        onClick={() => handleRemovePhrase(index)}
                        disabled={isSavingPhrases}
                        className="p-1 hover:bg-red-100 rounded transition-colors flex-shrink-0 disabled:opacity-40"
                        title="Remove phrase"
                      >
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newUserPhrase}
                    onChange={(e) => setNewUserPhrase(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddPhrase();
                      }
                    }}
                    placeholder="Add a phrase example..."
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={handleAddPhrase}
                    disabled={!newUserPhrase.trim() || isSavingPhrases}
                    className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSavingPhrases ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  </button>
                </div>
                {examplePhrases.length === 0 && (
                  <p className="text-xs text-gray-400 italic mt-2">No phrases added yet</p>
                )}
              </div>
            )}
          </div>
          </div>
          )}
          </div>
        </div>
      </div>

      {/* Close Confirmation Modal */}
      {showCloseConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">Discard changes?</h2>
                  <p className="text-sm text-gray-600">
                    You have unsaved changes. If you close now, they will be lost.
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => setShowCloseConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Keep editing
                </button>
                <button
                  onClick={handleDiscard}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
                >
                  Discard
                </button>
                <button
                  onClick={handleSaveAndClose}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                  {isSaving ? (
                    <>
                      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save and close'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Practice Card Modal */}
      {showPracticeCard && card && (
        <PracticeCard
          cardId={card.id}
          cardName={card.name}
          onClose={() => setShowPracticeCard(false)}
        />
      )}

      {/* Update Card AI Modal */}
      {showUpdateAI && card && (
        <UpdateCardAI
          card={card}
          onClose={() => setShowUpdateAI(false)}
          onSuccess={async () => {
            setShowUpdateAI(false);
            await loadCard(); // Reload card in place; stay on card view (do not call onSave — parent would reload/close)
          }}
        />
      )}

      {/* Practice & Explore Picker */}
      {showCardPracticePicker && card && (
        <CardPracticePickerModal
          cardName={card.name}
          onClose={() => setShowCardPracticePicker(false)}
          onFlashcard={() => setShowPracticeCard(true)}
          onGeneratePhrases={() => setShowGeneratePhrases(true)}
          onGenerateCollocations={() => setShowCollocations(true)}
          onGenerateSynonyms={() => setShowSynonyms(true)}
          onGenerateAntonyms={() => setShowAntonyms(true)}
          onGenerateImage={() => setShowGenerateImage(true)}
          onUpdateCardAI={isInstanceOwner ? () => setShowUpdateAI(true) : undefined}
        />
      )}

      {/* Card Q&A Practice */}
      {showCardContextualPractice && card && (
        <ContextualPracticeModal
          cardId={card.id}
          categoryName={card.name}
          leafCardCount={1}
          onClose={() => setShowCardContextualPractice(false)}
        />
      )}

      {/* Generate Phrases Modal */}
      {showGeneratePhrases && card && (
        <GeneratePhrasesModal
          cardId={card.id}
          cardName={card.name}
          onClose={() => setShowGeneratePhrases(false)}
          onAddPhrase={handleAddPhraseFromGenerated}
        />
      )}

      {/* Generate Collocations Modal */}
      {showCollocations && card && (
        <GenerateCollocationsModal
          cardId={card.id}
          cardName={card.name}
          onClose={() => setShowCollocations(false)}
          onAddCollocation={handleAddCollocationFromGenerated}
        />
      )}

      {/* Generate Synonyms Modal */}
      {showSynonyms && card && (
        <GenerateSynonymsModal
          cardId={card.id}
          cardName={card.name}
          onClose={() => setShowSynonyms(false)}
          onAddSynonym={handleAddSynonymFromGenerated}
        />
      )}

      {/* Generate Antonyms Modal */}
      {showAntonyms && card && (
        <GenerateAntonymsModal
          cardId={card.id}
          cardName={card.name}
          onClose={() => setShowAntonyms(false)}
          onAddAntonym={handleAddAntonymFromGenerated}
        />
      )}

      {/* Generate Image Modal */}
      {showGenerateImage && card && (
        <GenerateImageModal
          cardId={card.id}
          cardName={card.name}
          onClose={() => setShowGenerateImage(false)}
          onSaved={() => loadCard()}
        />
      )}

      {/* Move To Modal */}
      {showMoveToModal && card && categories.length > 0 && (
        <MoveToModal
          item={{
            id: card.id,
            name: card.name,
            type: 'card',
            parent_id: card.parent_id,
            is_folder: false,
            children: [],
            category_id: card.category_id ?? undefined,
          }}
          categories={categories.map(cat => ({
            id: cat.id,
            name: cat.name,
            isExpanded: false,
            children: [],
          }))}
          onClose={() => setShowMoveToModal(false)}
          onMove={handleMove}
        />
      )}

      {/* Practice Phrases Modal */}
      {showPracticePhrases && card && (
        <PracticePhrases
          cardId={card.id}
          cardName={card.name}
          onClose={() => setShowPracticePhrases(false)}
        />
      )}

      {/* PDF error toast */}
      {pdfToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white text-sm px-4 py-2.5 rounded-lg shadow-lg">
          {pdfToast}
        </div>
      )}
    </>
  );
};
